import React, { createContext, useState, useCallback, useEffect, useRef, useContext } from "react";
import { messagingApi } from "../api/messagingApi";
import io from "socket.io-client";
import { AuthContext } from "./AuthContext";

export const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [latestMessage, setLatestMessage] = useState(null);
  const socketRef = useRef(null);

  const { userId, token: userToken } = useContext(AuthContext);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await messagingApi.getNotifications(userId);
      if (response?.data?.notifications) setNotifications(response.data.notifications);
    } catch (err) {
      console.error(err);
    }
  }, [userId]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await messagingApi.getUnreadCount(userId);
      if (response?.data?.unreadCount !== undefined) setUnreadCount(response.data.unreadCount);
    } catch (err) {
      console.error(err);
    }
  }, [userId]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      await messagingApi.markNotificationAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
      );
      fetchUnreadCount();
    } catch (err) {
      console.error(err);
    }
  }, [fetchUnreadCount]);

  const markAllAsRead = useCallback(async () => {
    try {
      await messagingApi.markAllNotificationsAsRead(userId);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error(err);
    }
  }, [userId]);

  const addNotification = useCallback((newNotification) => {
    setNotifications((prev) => [newNotification, ...prev]);
    setUnreadCount((prev) => prev + 1);
  }, []);

  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await messagingApi.deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      fetchUnreadCount();
    } catch (err) {
      console.error(err);
    }
  }, [fetchUnreadCount]);

  const clearAllNotifications = useCallback(async () => {
    try {
      await messagingApi.clearAllNotifications(userId);
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error(err);
    }
  }, [userId]);

  // ----------------- Socket.IO Setup -----------------
  useEffect(() => {
    if (!userId || !userToken) return;

    const socket = io(process.env.REACT_APP_SOCKET_URL || "https://webback.opencurtainscasting.com", {
      auth: { token: userToken },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    // Join user's room after connecting
    socket.on("connection", () => {
      console.log("Socket connected:", socket.id);
    });

    // Notifications
    socket.on("newNotification", addNotification);
    socket.on("notificationRead", (id) => {
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
      );
    });
    socket.on("allNotificationsRead", () => {
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    });

    // Messages
    socket.on("receiveMessage", (message) => {
      console.log('=============', message);
      setLatestMessage(message);
    });

    // Cleanup
    return () => socket.disconnect();
  }, [userId, userToken, addNotification]);

  // Initial fetch
  useEffect(() => {
    if (userId) {
      fetchNotifications();
      fetchUnreadCount();
    }
  }, [userId, fetchNotifications, fetchUnreadCount]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        addNotification,
        deleteNotification,
        clearAllNotifications,
        fetchNotifications,
        fetchUnreadCount,
        socket: socketRef.current,
        latestMessage,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
